import { useEffect, useMemo, useState } from 'react'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  DollarSign,
  FileText,
  Hash,
  Loader2,
  Search,
  ShieldCheck,
  Star,
  Trophy,
  type LucideIcon,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useListadoFidelizacion } from '../hooks/useFidelizacion'
import { formatMoneda, formatNumero, terminoComprobante } from '../lib/paisMapa'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { parseFidelizacionError } from '../hooks/useFidelizacionError'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface ColumnDef {
  key: string
  label: string
  icon?: LucideIcon
  accessor?: (row: Record<string, unknown>) => unknown
  format?: (v: unknown, row: Record<string, unknown>) => React.ReactNode
  align?: 'left' | 'right'
}

const TZ = 'America/Guayaquil'

function formatDate(v: unknown): string {
  if (!v) return '—'
  const s = String(v)
  // Fecha-only (YYYY-MM-DD): sin componente horario. Se formatea a dd/mm/yy a
  // partir del string, sin construir un Date para no desplazar el día por la
  // zona horaria.
  const soloFecha = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (soloFecha) {
    const [, y, m, d] = soloFecha
    return `${d}/${m}/${y.slice(2)}`
  }
  try {
    const date = new Date(s)
    if (Number.isNaN(date.getTime())) return s
    // Se usa en-US para obtener un dayPeriod AM/PM fiable y se mapea al formato
    // pedido "a.m." / "p.m.".
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: TZ,
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).formatToParts(date)
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
    const periodo = get('dayPeriod').toLowerCase().startsWith('p') ? 'p.m.' : 'a.m.'
    return `${get('day')}/${get('month')}/${get('year')}, ${get('hour')}:${get('minute')} ${periodo}`
  } catch {
    return s
  }
}

function formatDecimal(v: unknown, configId: number, decimals = 2): string {
  if (v === null || v === undefined || v === '') return '—'
  const n = typeof v === 'string' ? parseFloat(v) : (v as number)
  if (Number.isNaN(n)) return String(v)
  return formatNumero(n, configId, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

function renderEstadoBadge(v: unknown): React.ReactNode {
  if (!v) return '—'
  const raw = String(v)
  const norm = raw.toLowerCase().replace(/[_\s]+/g, ' ').trim()
  const green = ['aprobada', 'confirmada', 'validada', 'corregida']
  const red = ['fecha invalida', 'fecha inválida', 'factura incompleta', 'rechazada']
  const yellow = ['sin productos', 'duplicada', 'pendiente']
  const cls = green.includes(norm)
    ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
    : red.includes(norm)
      ? 'border-red-500 text-red-600 dark:text-red-400'
      : yellow.includes(norm)
        ? 'border-yellow-500 text-yellow-600 dark:text-yellow-400'
        : 'border-gray-400 text-gray-600 dark:text-gray-300'
  return (
    <Badge variant="outline" className={`capitalize text-xs ${cls}`}>
      {raw}
    </Badge>
  )
}

function resolverPuntos(row: Record<string, unknown>) {
  const otorgados = row.puntos_otorgados
  const corregidosRaw = row.puntos_corregidos
  const corregidosNum =
    typeof corregidosRaw === 'string' ? parseFloat(corregidosRaw) : (corregidosRaw as number)
  const tieneCorreccion =
    corregidosRaw !== null &&
    corregidosRaw !== undefined &&
    corregidosRaw !== '' &&
    !Number.isNaN(corregidosNum) &&
    corregidosNum !== 0
  return {
    valor: tieneCorreccion ? corregidosRaw : otorgados,
    esCorregido: tieneCorreccion,
    otorgados,
    corregidos: corregidosRaw,
  }
}

function buildColumns(configId: number): ColumnDef[] {
  return [
  { key: 'id', label: 'ID', icon: Hash },
  { key: 'fecha_emision', label: 'Emisión', icon: Calendar, format: (v) => formatDate(v) },
  { key: 'establecimiento', label: 'Establecimiento', icon: Building2 },
  { key: 'numero_factura', label: `Núm. ${terminoComprobante(configId)}`, icon: FileText },
  {
    key: 'monto',
    label: 'Monto bruto',
    icon: Trophy,
    format: (v) => formatMoneda(v as number | string | null | undefined, configId),
    align: 'right',
  },
  {
    key: 'monto_participantes',
    label: 'Monto programa',
    icon: DollarSign,
    format: (v) => formatMoneda(v as number | string | null | undefined, configId),
    align: 'right',
  },
  {
    key: 'puntos',
    label: 'Puntos',
    icon: Star,
    accessor: (r) => resolverPuntos(r).valor,
    format: (_v, row) => {
      const { valor, esCorregido, otorgados, corregidos } = resolverPuntos(row)
      if (!esCorregido) return formatDecimal(valor, configId)
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="underline decoration-dotted underline-offset-2 cursor-help">
                {formatDecimal(valor, configId)}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Puntos corregidos: {formatDecimal(corregidos, configId)} · Original: {formatDecimal(otorgados, configId)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    },
    align: 'right',
  },
  { key: 'estado', label: 'Estado', icon: ShieldCheck, format: (v) => renderEstadoBadge(v) },
  ]
}

function getCellValue(row: Record<string, unknown>, col: ColumnDef): unknown {
  return col.accessor ? col.accessor(row) : row[col.key]
}

/**
 * Valor de ordenamiento client-side para una fila (modo `datosOverride`). Las
 * columnas numéricas/fecha se comparan como número; el resto como texto. Refleja
 * las mismas llaves que el ordenamiento server-side (`SORT_KEY_TO_ORDERING`).
 */
function valorParaOrden(row: Record<string, unknown>, key: string): number | string {
  if (key === 'fecha_emision') return Date.parse(String(row.fecha_emision ?? '')) || 0
  if (key === 'puntos') {
    const v = resolverPuntos(row).valor
    return typeof v === 'string' ? parseFloat(v) || 0 : (v as number) || 0
  }
  if (key === 'monto' || key === 'monto_participantes' || key === 'id') {
    return Number(row[key] ?? 0) || 0
  }
  return String(row[key] ?? '')
}

function matchesBusqueda(row: Record<string, unknown>, term: string): boolean {
  const lower = term.toLowerCase()
  return Object.values(row).some(
    (val) => val != null && String(val).toLowerCase().includes(lower),
  )
}

export interface TablaFacturasFidelizacionProps {
  configId: number
  titulo?: string
  /** Filtros adicionales mandados al endpoint (provincia, cadena, etc + rango). */
  filtros?: Record<string, string | number | boolean | undefined>
  /** Filtro client-side aplicado tras el fetch (ej. limitar estados). */
  rowFilter?: (row: Record<string, unknown>) => boolean
  /**
   * Filas provistas por el consumidor para renderizar client-side (búsqueda,
   * orden y paginación se hacen en el cliente). Cuando es distinto de `null`
   * se ignora el fetch server-side. Se usa en Ventas para el filtro de producto,
   * que el upstream no soporta en el listado de facturas.
   */
  datosOverride?: Record<string, unknown>[] | null
  /** Estado de carga de la fuente `datosOverride` (loader + skeleton). */
  overrideLoading?: boolean
  /** Callback al clickear "Ver factura". */
  onComprobanteClick?: (facturaId: number | string, row: Record<string, unknown>) => void
}

export function TablaFacturasFidelizacion({
  configId,
  titulo = 'Facturas',
  filtros,
  rowFilter,
  datosOverride = null,
  overrideLoading = false,
  onComprobanteClick,
}: TablaFacturasFidelizacionProps) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const columns = useMemo(() => buildColumns(configId), [configId])
  const [busqueda, setBusqueda] = useState('')
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortAsc, setSortAsc] = useState(true)

  // Reset page cuando cambian los filtros, el sort o el dataset client-side
  useEffect(() => { setPage(1) }, [filtros, sortKey, datosOverride])

  const SORT_KEY_TO_ORDERING: Record<string, string> = {
    fecha_emision: sortAsc ? 'fecha_emision_asc' : 'fecha_emision_desc',
    monto: sortAsc ? 'total_asc' : 'total_desc',
    monto_participantes: sortAsc ? 'total_asc' : 'total_desc',
    puntos: sortAsc ? 'puntos_asc' : 'puntos_desc',
  }

  const queryParams = useMemo(() => {
    const out: Record<string, string | number | boolean | undefined> = {
      page,
      page_size: pageSize,
    }
    if (filtros) {
      for (const [k, v] of Object.entries(filtros)) {
        if (v !== undefined && v !== null && v !== '') out[k] = v
      }
    }
    if (sortKey && SORT_KEY_TO_ORDERING[sortKey]) {
      out.ordering = SORT_KEY_TO_ORDERING[sortKey]
    }
    return out
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filtros, sortKey, sortAsc])

  const usandoOverride = datosOverride != null

  const { data, isLoading, isFetching, error } = useListadoFidelizacion(
    configId,
    'facturas',
    queryParams,
    { enabled: !usandoOverride },
  )

  // Filas ya filtradas por búsqueda/rowFilter. En modo override también se
  // ordenan aquí (client-side); en modo server el orden lo aplica el upstream.
  const filteredRows = useMemo(() => {
    let rows = usandoOverride
      ? (datosOverride as Record<string, unknown>[])
      : ((data?.items as Record<string, unknown>[]) ?? [])
    if (rowFilter) rows = rows.filter(rowFilter)
    if (busqueda) rows = rows.filter((r) => matchesBusqueda(r, busqueda))
    if (usandoOverride && sortKey) {
      const dir = sortAsc ? 1 : -1
      rows = [...rows].sort((a, b) => {
        const av = valorParaOrden(a, sortKey)
        const bv = valorParaOrden(b, sortKey)
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
        return String(av).localeCompare(String(bv), 'es') * dir
      })
    }
    return rows
  }, [usandoOverride, datosOverride, data, rowFilter, busqueda, sortKey, sortAsc])

  // En modo override la paginación es client-side; en modo server ya viene
  // paginado desde el upstream.
  const processedRows = useMemo(() => {
    if (!usandoOverride) return filteredRows
    const start = (page - 1) * pageSize
    return filteredRows.slice(start, start + pageSize)
  }, [usandoOverride, filteredRows, page, pageSize])

  const total = usandoOverride ? filteredRows.length : (data?.total ?? 0)
  const totalPages = usandoOverride
    ? Math.max(1, Math.ceil(filteredRows.length / pageSize))
    : (data?.total_pages ?? 1)

  const handleSort = (col: string) => {
    if (sortKey === col) {
      if (!sortAsc) {
        setSortKey(null)
        setSortAsc(true)
      } else {
        setSortAsc(false)
      }
    } else {
      setSortKey(col)
      setSortAsc(true)
    }
  }

  const cargandoInicial = usandoOverride ? overrideLoading : (isLoading && !data)
  const actualizando = usandoOverride ? overrideLoading : isFetching

  if (cargandoInicial) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border border-gray-300 dark:border-gray-700 w-full">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg">{titulo}</CardTitle>
            <Badge
              variant="outline"
              className="text-xs font-semibold tabular-nums border-[0.5px] border-emerald-500 rounded-full"
            >
              {total.toLocaleString()}
            </Badge>
            {actualizando && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Actualizando...
              </span>
            )}
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar en la página..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-3">
            <AlertDescription>{parseFidelizacionError(error).message}</AlertDescription>
          </Alert>
        )}

        <div className="rounded-md border border-gray-300 dark:border-gray-700 overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
              <TableRow>
                {columns.map((col) => {
                  const Icon = col.icon
                  const isSorted = sortKey === col.key
                  const SortIcon = isSorted ? (sortAsc ? ArrowUp : ArrowDown) : ArrowUpDown
                  return (
                    <TableHead
                      key={col.key}
                      className={`select-none whitespace-nowrap cursor-pointer hover:text-foreground ${
                        col.align === 'right' ? 'text-right' : ''
                      }`}
                      onClick={() => handleSort(col.key)}
                    >
                      <div
                        className={`flex items-center gap-1.5 capitalize ${
                          col.align === 'right' ? 'justify-end' : ''
                        }`}
                      >
                        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
                        {col.label}
                        <SortIcon
                          className={`h-3 w-3 ${
                            isSorted ? 'text-emerald-500' : 'text-muted-foreground/50'
                          }`}
                        />
                      </div>
                    </TableHead>
                  )
                })}
                {onComprobanteClick && <TableHead className="w-10" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {processedRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (onComprobanteClick ? 1 : 0)}
                    className="text-center py-8 text-muted-foreground"
                  >
                    {`No se encontraron ${terminoComprobante(configId, { plural: true })}`}
                  </TableCell>
                </TableRow>
              ) : (
                processedRows.map((row, idx) => (
                  <TableRow key={String(row.id ?? idx)}>
                    {columns.map((col) => {
                      const value = getCellValue(row, col)
                      return (
                        <TableCell
                          key={col.key}
                          className={`whitespace-nowrap max-w-[300px] truncate ${
                            col.align === 'right' ? 'text-right tabular-nums' : ''
                          }`}
                        >
                          {col.format ? col.format(value, row) : (value as React.ReactNode) ?? '—'}
                        </TableCell>
                      )
                    })}
                    {onComprobanteClick && (
                      <TableCell>
                        {row.id != null && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title={`Ver ${terminoComprobante(configId)}`}
                            onClick={() =>
                              onComprobanteClick(row.id as number | string, row)
                            }
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Paginación */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Mostrar</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v))
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[80px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">por página</span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(1)}
              disabled={page <= 1 || isFetching}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage((p) => p - 1)}
              disabled={page <= 1 || isFetching}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground min-w-[100px] text-center">
              {page} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages || isFetching}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages || isFetching}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
